import { useEffect, useMemo, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiCheck,
  FiDollarSign,
  FiEdit2,
  FiPhone,
  FiSearch,
  FiTruck,
  FiUsers,
  FiTrendingUp,
  FiX,
  FiPrinter,
  FiDownload,
  FiUser,
  FiImage,
  FiLock,
} from "react-icons/fi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { hasPermission } from "../utils/permissions";
import {
  getOutstandingSuppliers,
  updateOutstandingAmount,
} from "../../services/outstanding_service";

import "./outstanding.css";

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const dateFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const AMOUNT_TIERS = {
  high: 200000,
  medium: 100000,
};

function getAmountTier(amount) {
  const value = Number(amount || 0);
  if (value > AMOUNT_TIERS.high) return "high";
  if (value >= AMOUNT_TIERS.medium) return "medium";
  return "low";
}
const OUTSTANDING_RANGES = [
  { label: "0 - 50K", min: 0, max: 50000 },
  { label: "50K - 1L", min: 50000, max: 100000 },
  { label: "1L - 2L", min: 100000, max: 200000 },
  { label: "2L - 3L", min: 200000, max: 300000 },
  { label: "3L - 4L", min: 300000, max: 400000 },
  { label: "4L - 5L", min: 400000, max: 500000 },
  { label: "5L - 6L", min: 500000, max: 600000 },
  { label: "6L - 7L", min: 600000, max: 700000 },
  { label: "7L - 8L", min: 700000, max: 800000 },
  { label: "8L - 9L", min: 800000, max: 900000 },
  { label: "9L - 10L", min: 900000, max: 1000000 },
  { label: "10L+", min: 1000000, max: null },
];

function isWithinOutstandingRange(amount, rangeIndex) {
  if (rangeIndex === "" || rangeIndex === undefined || rangeIndex === null) return true;
  const range = OUTSTANDING_RANGES[Number(rangeIndex)];
  if (!range) return true;
  const value = Number(amount || 0);
  if (value < range.min) return false;
  if (range.max !== null && value >= range.max) return false;
  return true;
}

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const AVATAR_PALETTE = [
  { bg: "#e0e7ff", color: "#4338ca" },
  { bg: "#fce7f3", color: "#be185d" },
  { bg: "#dcfce7", color: "#15803d" },
  { bg: "#fef3c7", color: "#a16207" },
  { bg: "#dbeafe", color: "#1d4ed8" },
  { bg: "#fee2e2", color: "#b91c1c" },
];

function avatarStyle(name) {
  if (!name) return AVATAR_PALETTE[0];
  const idx = name.charCodeAt(0) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx];
}

const ASSIGNMENT_TYPE_BADGE = {
  Dispatch: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  Handover: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  Recovery: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
};

function assignmentTypeBadgeStyle(name) {
  return ASSIGNMENT_TYPE_BADGE[name] || { bg: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
}

function getPageNumbers(current, total) {
  const pages = [];
  const window = 1;

  for (let p = 1; p <= total; p++) {
    const isEdge = p === 1 || p === total;
    const isNearCurrent = Math.abs(p - current) <= window;
    if (isEdge || isNearCurrent) {
      pages.push(p);
    } else if (pages[pages.length - 1] !== "…") {
      pages.push("…");
    }
  }

  return pages;
}

function slug(str) {
  return String(str || "")
    .trim()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}
function toDateOnly(d) {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
}

function isWithinDateRange(dateValue, fromDate, toDate) {
  if (!fromDate && !toDate) return true;
  if (!dateValue) return false;

  const d = toDateOnly(dateValue);
  if (!d) return false;

  if (fromDate) {
    const from = toDateOnly(fromDate);
    if (from && d < from) return false;
  }
  if (toDate) {
    const to = toDateOnly(toDate);
    if (to && d > to) return false;
  }
  return true;
}

export default function OutstandingSuppliers() {
  const canEdit = hasPermission("outstanding_update");
  const canView = hasPermission("outstanding_view") || canEdit;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedOutstandingRange, setSelectedOutstandingRange] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [editError, setEditError] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const data = await getOutstandingSuppliers();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load outstanding suppliers.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [canView]);

  const yearOptions = useMemo(() => {
    const years = new Set();
    rows.forEach((row) => {
      const y = row.vehicle?.year_of_make;
      if (y !== null && y !== undefined && y !== "") years.add(Number(y));
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [rows]);

 const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    return rows.filter((row) => {
      const supplierName = row.supplier?.supplier_name?.toLowerCase() || "";
      const supplierCode = row.supplier?.supplier_code?.toLowerCase() || "";
      const regNumber = row.vehicle?.vehicle_registration_number?.toLowerCase() || "";
      const modelName = row.vehicle?.model_name?.toLowerCase() || "";
      const mobile = row.supplier?.mobile?.toLowerCase() || "";
      const altMobile = row.supplier?.alternate_mobile?.toLowerCase() || "";
      const supplierType = row.supplier?.supplier_type_name?.toLowerCase() || "";
      const assignmentType = row.assignment_type_name?.toLowerCase() || "";

      const matchesSearch =
        !q ||
        supplierName.includes(q) ||
        supplierCode.includes(q) ||
        regNumber.includes(q) ||
        modelName.includes(q) ||
        mobile.includes(q) ||
        altMobile.includes(q) ||
        supplierType.includes(q) ||
        assignmentType.includes(q);

      const matchesDateRange = isWithinDateRange(row.created_at, fromDate, toDate);
      const matchesOutstandingRange = isWithinOutstandingRange(
        row.outstanding_amount,
        selectedOutstandingRange
      );
      const matchesYear =
        selectedYear === "" ||
        Number(row.vehicle?.year_of_make) === Number(selectedYear);

      return matchesSearch && matchesDateRange && matchesOutstandingRange && matchesYear;
    });
  }, [rows, search, fromDate, toDate, selectedOutstandingRange, selectedYear]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, rows, fromDate, toDate, selectedOutstandingRange, selectedYear]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedRows = useMemo(() => {
    const start = (safeCurrentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, safeCurrentPage]);

  const totalOutstanding = useMemo(() => {
    return filteredRows.reduce(
      (sum, row) => sum + Number(row.outstanding_amount || 0),
      0
    );
  }, [filteredRows]);

  const uniqueSupplierCount = useMemo(() => {
    const ids = new Set(
      filteredRows.map((row) => row.supplier?.supplier_code || row.supplier?.supplier_name)
    );
    return ids.size;
  }, [filteredRows]);

  const highDuesCount = useMemo(
    () => filteredRows.filter((row) => getAmountTier(row.outstanding_amount) === "high").length,
    [filteredRows]
  );

  const hasDateFilter = Boolean(fromDate || toDate);
  const hasAnyFilter = Boolean(
    search || hasDateFilter || selectedOutstandingRange !== "" || selectedYear !== ""
  );

  function clearDateFilter() {
    setFromDate("");
    setToDate("");
  }

  function resetAllFilters() {
    setSearch("");
    setFromDate("");
    setToDate("");
    setSelectedOutstandingRange("");
    setSelectedYear("");
  }

  function startEdit(row) {
    if (!canEdit) return;
    setEditError(null);
    setEditingId(row.id);
    setEditValue(String(row.outstanding_amount ?? ""));
  }

  function cancelEdit() {
    setEditingId(null);
    setEditValue("");
    setEditError(null);
  }

  async function saveEdit(row) {
    if (!canEdit) return;

    const parsed = Number(editValue);

    if (editValue.trim() === "" || Number.isNaN(parsed) || parsed < 0) {
      setEditError("Enter a valid non-negative amount.");
      return;
    }

    if (parsed === Number(row.outstanding_amount)) {
      cancelEdit();
      return;
    }

    setSavingId(row.id);
    setEditError(null);

    try {
      const updated = await updateOutstandingAmount(
        row.id,
        parsed,
        row.service_location_id ?? null
      );

      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                ...updated,
                outstanding_amount: updated?.outstanding_amount ?? parsed,
                vehicle: updated?.vehicle ?? r.vehicle,
                supplier: updated?.supplier ?? r.supplier,
                assignment_type_name: updated?.assignment_type_name ?? r.assignment_type_name,
              }
            : r
        )
      );

      setViewRow((prev) =>
        prev && prev.id === row.id
          ? { ...prev, outstanding_amount: updated?.outstanding_amount ?? parsed }
          : prev
      );

      cancelEdit();
    } catch (err) {
      setEditError(err.message || "Failed to update outstanding amount.");
    } finally {
      setSavingId(null);
    }
  }

  function handleEditKeyDown(e, row) {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit(row);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  }

  function openView(row) {
    setViewRow(row);
  }

  function closeView() {
    setViewRow(null);
  }

  function handlePrint() {
    window.print();
  }
async function handleDownload() {
  if (!printRef.current || isGeneratingPdf) return;

  setIsGeneratingPdf(true);

  const wrap = printRef.current;
  const bodyEl = wrap.querySelector(".outstanding-viewmodal-body");
  const originalWrapStyle = wrap.style.cssText;
  const originalBodyStyle = bodyEl ? bodyEl.style.cssText : "";

  try {
    wrap.style.maxHeight = "none";
    wrap.style.height = "auto";
    wrap.style.overflow = "visible";

    if (bodyEl) {
      bodyEl.style.maxHeight = "none";
      bodyEl.style.height = "auto";
      bodyEl.style.overflow = "visible";
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    const canvas = await html2canvas(wrap, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
      ignoreElements: (el) => el.classList?.contains("no-print"),
      windowWidth: wrap.scrollWidth,
      windowHeight: wrap.scrollHeight,
      height: wrap.scrollHeight,
      width: wrap.scrollWidth,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    const margin = 24;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;

    const imgWidth = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let renderedHeight = 0;

    pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight);
    heightLeft -= usableHeight;
    renderedHeight += usableHeight;

    while (heightLeft > 0) {
      pdf.addPage();
      pdf.addImage(
        imgData,
        "PNG",
        margin,
        margin - renderedHeight,
        imgWidth,
        imgHeight
      );
      heightLeft -= usableHeight;
      renderedHeight += usableHeight;
    }

    const supplierPart = slug(viewRow?.supplier?.supplier_name) || "supplier";
    const vehiclePart = slug(viewRow?.vehicle?.vehicle_registration_number) || "vehicle";
    pdf.save(`outstanding-${supplierPart}-${vehiclePart}.pdf`);
  } catch (err) {
    console.error("Failed to generate PDF:", err);
    alert("Something went wrong generating the PDF. Please try again.");
  } finally {
    wrap.style.cssText = originalWrapStyle;
    if (bodyEl) bodyEl.style.cssText = originalBodyStyle;
    setIsGeneratingPdf(false);
  }
}
  if (!canView) {
    return (
      <div className="admin-layout">
        <Sidebar />
        <div className="admin-main">
          <Header />
          <div className="outstanding-page">
            <div className="outstanding-state outstanding-error">
              <FiLock size={22} />
              <span>You don't have permission to view outstanding dues.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Sidebar />

      <div className="admin-main">
        <Header />

        <div className="outstanding-page">
          <style>{`
            .outstanding-table-wrap--scroll::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          <div className="outstanding-stats">
            <div className="outstanding-stat outstanding-stat--primary">
              <div className="outstanding-stat-icon">
                <FiDollarSign />
              </div>
              <div>
                <span className="outstanding-stat-label">Total Outstanding</span>
                <span className="outstanding-stat-value">
                  {currencyFormatter.format(totalOutstanding)}
                </span>
              </div>
            </div>

            <div className="outstanding-stat">
              <div className="outstanding-stat-icon outstanding-stat-icon--indigo">
                <FiUsers />
              </div>
              <div>
                <span className="outstanding-stat-label">Suppliers with Dues</span>
                <span className="outstanding-stat-value outstanding-stat-value--dark">
                  {uniqueSupplierCount}
                </span>
              </div>
            </div>

            <div className="outstanding-stat">
              <div className="outstanding-stat-icon outstanding-stat-icon--danger">
                <FiTrendingUp />
              </div>
              <div>
                <span className="outstanding-stat-label">High Priority (&gt; ₹2L)</span>
                <span className="outstanding-stat-value outstanding-stat-value--dark">
                  {highDuesCount}
                </span>
              </div>
            </div>
          </div>

          <div
            className="outstanding-toolbar"
            style={{ flexWrap: "wrap", rowGap: 8 }}
          >
            <div className="outstanding-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Search supplier vehicle model mobile "
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="outstanding-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <FiX />
                </button>
              )}
            </div>
            <div className="date-field" style={{ flex: "0 0 auto" }}>
              <input
                type="date"
                className="date-input"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
                title="Filter by recorded date — from"
              />
            </div>

            <div className="date-field" style={{ flex: "0 0 auto" }}>
              <input
                type="date"
                className="date-input"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
                title="Filter by recorded date — to"
              />
            </div>

            <div className="date-field outstanding-range-field" style={{ flex: "0 0 auto" }}>
              <select
                className="date-input"
                value={selectedOutstandingRange}
                onChange={(e) => setSelectedOutstandingRange(e.target.value)}
                title="Filter by outstanding amount"
                style={{ cursor: "pointer" }}
              >
                <option value="">All Amounts</option>
                {OUTSTANDING_RANGES.map((range, idx) => (
                  <option key={range.label} value={idx}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="date-field outstanding-year-field" style={{ flex: "0 0 auto" }}>
              <select
                className="date-input"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                title="Filter by vehicle year of make"
                style={{ cursor: "pointer" }}
              >
                <option value="">Vehicle Make</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* {hasAnyFilter && (
              <button
                type="button"
                className="outstanding-search-clear"
                onClick={resetAllFilters}
                style={{
                  position: "static",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 12.5,
                  color: "#6b7280",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  padding: "6px 10px",
                  background: "#fff",
                }}
              >
                Reset
              </button>
            )} */}

            {hasAnyFilter && (
              <span className="outstanding-result-count">
                {filteredRows.length} of {rows.length} records
              </span>
            )}
          </div>

          {hasDateFilter && (
            <div className="filter-summary">
              Showing records recorded
              {fromDate && <strong>&nbsp;from {new Date(fromDate).toLocaleDateString("en-GB")}</strong>}
              {toDate && <strong>&nbsp;to {new Date(toDate).toLocaleDateString("en-GB")}</strong>}
              <button className="filter-summary-clear" onClick={clearDateFilter}>
                Clear dates
              </button>
            </div>
          )}
          {loading && (
            <div className="outstanding-state">
              <div className="outstanding-spinner" />
              Loading outstanding dues…
            </div>
          )}

          {!loading && error && (
            <div className="outstanding-state outstanding-error">
              <FiAlertCircle size={22} />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && filteredRows.length === 0 && (
            <div className="outstanding-state outstanding-empty">
              <FiDollarSign size={30} className="outstanding-empty-icon" />
              <p>
                {hasAnyFilter
                  ? "No outstanding records match your filters."
                  : "No outstanding dues — all clear!"}
              </p>
              {hasAnyFilter && (
                <button
                  type="button"
                  className="outstanding-empty-clear"
                  onClick={resetAllFilters}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* ── Table ── */}
          {!loading && !error && filteredRows.length > 0 && (
            <div
              className="outstanding-table-wrap outstanding-table-wrap--scroll"
            >
              <table className="outstanding-table">
                <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "#fff" }}>
                  <tr>
                    <th>Vehicle</th>
                    <th>Supplier</th>
                    <th>Type</th>
                    <th>Assignment</th>
                    <th>Mobile</th>
                    <th>Recorded On</th>
                    <th className="col-numeric">Outstanding</th>
                    {canEdit && <th className="col-action">Action</th>}
                  </tr>
                </thead>

                <tbody>
                  {paginatedRows.map((row) => {
                    const supplierName = row.supplier?.supplier_name;
                    const style = avatarStyle(supplierName);
                    const tier = getAmountTier(row.outstanding_amount);
                    const isEditing = canEdit && editingId === row.id;
                    const isSaving = savingId === row.id;
                    const assignmentBadge = assignmentTypeBadgeStyle(row.assignment_type_name);

                    return (
                      <tr
                        key={row.id}
                        className="outstanding-row-clickable"
                        onClick={() => !isEditing && openView(row)}
                      >
                        <td>
                          <div className="cell-primary">
                            <FiTruck className="inline-icon" />
                            {row.vehicle?.vehicle_registration_number || "—"}
                          </div>
                          <div className="cell-secondary">
                            {row.vehicle?.model_name || "—"}
                            {row.vehicle?.year_of_make ? ` · ${row.vehicle.year_of_make}` : ""}
                          </div>
                        </td>

                        <td>
                          <div className="supplier-cell">
                            <div
                              className="supplier-avatar"
                              style={{ background: style.bg, color: style.color }}
                            >
                              {getInitials(supplierName)}
                            </div>
                            <div>
                              <div className="cell-primary">{supplierName || "—"}</div>
                              <div className="cell-secondary">
                                {row.supplier?.supplier_code || "—"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>
                          {row.supplier?.supplier_type_name ? (
                            <span className="badge badge--role">
                              {row.supplier.supplier_type_name}
                            </span>
                          ) : (
                            <span className="cell-secondary cell-secondary--muted">—</span>
                          )}
                        </td>

                        <td>
                          {row.assignment_type_name ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                fontSize: 11.5,
                                fontWeight: 700,
                                padding: "3px 10px",
                                borderRadius: 999,
                                background: assignmentBadge.bg,
                                color: assignmentBadge.color,
                                border: `1px solid ${assignmentBadge.border}`,
                              }}
                            >
                              {row.assignment_type_name}
                            </span>
                          ) : (
                            <span className="cell-secondary cell-secondary--muted">—</span>
                          )}
                        </td>

                        <td>
                          {row.supplier?.mobile ? (
                            <div className="cell-primary">
                              <FiPhone className="inline-icon" />
                              {row.supplier.mobile}
                            </div>
                          ) : (
                            <span className="cell-secondary cell-secondary--muted">
                              No Mobile
                            </span>
                          )}
                          {row.supplier?.alternate_mobile && (
                            <div className="cell-secondary">
                              Alt: {row.supplier.alternate_mobile}
                            </div>
                          )}
                        </td>

                        <td className="cell-secondary">
                          {row.created_at
                            ? dateFormatter.format(new Date(row.created_at))
                            : "—"}
                        </td>

                        <td className="col-numeric" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <div className="amount-edit">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="amount-edit-input"
                                value={editValue}
                                autoFocus
                                disabled={isSaving}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => handleEditKeyDown(e, row)}
                              />
                              {editError && (
                                <div className="amount-edit-error">{editError}</div>
                              )}
                            </div>
                          ) : (
                            <span className={`amount-badge amount-badge--${tier}`}>
                              {currencyFormatter.format(row.outstanding_amount)}
                            </span>
                          )}
                        </td>

                        {canEdit && (
                          <td className="col-action" onClick={(e) => e.stopPropagation()}>
                            {isEditing ? (
                              <div className="action-btn-group">
                                <button
                                  type="button"
                                  className="update-btn update-btn--save"
                                  onClick={() => saveEdit(row)}
                                  disabled={isSaving}
                                >
                                  <FiCheck className="update-btn-icon" />
                                  {isSaving ? "Saving…" : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="update-btn update-btn--cancel"
                                  onClick={cancelEdit}
                                  disabled={isSaving}
                                  aria-label="Cancel edit"
                                >
                                  <FiX />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="update-btn"
                                onClick={() => startEdit(row)}
                              >
                                <FiEdit2 className="update-btn-icon" />
                                Update
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr>
                    <td colSpan={6} className="outstanding-footer-label">
                      Total ({filteredRows.length} record{filteredRows.length === 1 ? "" : "s"})
                    </td>
                    <td className="col-numeric outstanding-footer-value">
                      {currencyFormatter.format(totalOutstanding)}
                    </td>
                    {canEdit && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {!loading && !error && filteredRows.length > PAGE_SIZE && (
            <div className="outstanding-pagination">
              <span className="outstanding-pagination-info">
                Showing {(safeCurrentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(safeCurrentPage * PAGE_SIZE, filteredRows.length)} of{" "}
                {filteredRows.length}
              </span>

              <div className="outstanding-pagination-controls">
                <button
                  type="button"
                  className="outstanding-page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safeCurrentPage === 1}
                  aria-label="Previous page"
                >
                  ‹
                </button>

                {getPageNumbers(safeCurrentPage, totalPages).map((p, idx) =>
                  p === "…" ? (
                    <span key={`ellipsis-${idx}`} className="outstanding-page-ellipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`outstanding-page-btn${p === safeCurrentPage ? " outstanding-page-btn--active" : ""}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  )
                )}

                <button
                  type="button"
                  className="outstanding-page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safeCurrentPage === totalPages}
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
      {viewRow && (
        <div className="outstanding-viewmodal-overlay" onClick={closeView}>
          <div
            className="outstanding-viewmodal"
            onClick={(e) => e.stopPropagation()}
          >
            <div ref={printRef} className="outstanding-viewmodal-printwrap">
              <div className="outstanding-viewmodal-header">
                <div className="outstanding-viewmodal-header-left">
                  {viewRow.supplier?.photo_url ? (
                    <img
                      src={viewRow.supplier.photo_url}
                      alt={viewRow.supplier?.supplier_name || "Supplier"}
                      className="outstanding-viewmodal-avatar"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="outstanding-viewmodal-avatar outstanding-viewmodal-avatar-fallback">
                      <FiUser size={22} />
                    </div>
                  )}
                  <div>
                    <h3>{viewRow.supplier?.supplier_name || "—"}</h3>
                    <p>{viewRow.supplier?.supplier_code || "—"} · {viewRow.vehicle?.vehicle_registration_number || "—"}</p>
                  </div>
                </div>

                <div className="outstanding-viewmodal-header-actions no-print">
                  <button
                    type="button"
                    className="outstanding-viewmodal-action-btn"
                    onClick={handleDownload}
                    disabled={isGeneratingPdf}
                  >
                    <FiDownload /> {isGeneratingPdf ? "Generating…" : "Download PDF"}
                  </button>
                  <button type="button" className="outstanding-viewmodal-action-btn" onClick={handlePrint}>
                    <FiPrinter /> Print
                  </button>
                  <button type="button" className="outstanding-viewmodal-close" onClick={closeView} aria-label="Close">
                    <FiX />
                  </button>
                </div>
              </div>

              <div className="outstanding-viewmodal-body">

                <div className="outstanding-viewmodal-section">
                  <h4>Photos</h4>
                  <div className="outstanding-viewmodal-photos">
                    <div className="outstanding-viewmodal-photo-card">
                      <span className="outstanding-viewmodal-photo-label">Supplier Photo</span>
                      {viewRow.supplier?.photo_url ? (
                        <img src={viewRow.supplier.photo_url} alt="Supplier" crossOrigin="anonymous" />
                      ) : (
                        <div className="outstanding-viewmodal-photo-empty">
                          <FiUser size={24} />
                          <span>No photo</span>
                        </div>
                      )}
                    </div>

                    <div className="outstanding-viewmodal-photo-card">
  <span className="outstanding-viewmodal-photo-label">Vehicle Photo</span>
  {viewRow.vehicle?.vehicle_photo ? (
    <img src={viewRow.vehicle.vehicle_photo} alt="Vehicle" crossOrigin="anonymous" />
  ) : (
    <div className="outstanding-viewmodal-photo-empty">
      <FiImage size={24} />
      <span>No photo</span>
    </div>
  )}
</div>
                  </div>
                </div>

                <div className="outstanding-viewmodal-section">
                  <h4>Vehicle</h4>
                  <div className="outstanding-viewmodal-grid">
                    <div className="outstanding-viewmodal-field">
                      <span>Registration Number</span>
                      <strong>{viewRow.vehicle?.vehicle_registration_number || "—"}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Model</span>
                      <strong>{viewRow.vehicle?.model_name || "—"}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Year of Make</span>
                      <strong>{viewRow.vehicle?.year_of_make || "—"}</strong>
                    </div>
                  </div>
                </div>

                <div className="outstanding-viewmodal-section">
                  <h4>Supplier</h4>
                  <div className="outstanding-viewmodal-grid">
                    <div className="outstanding-viewmodal-field">
                      <span>Name</span>
                      <strong>{viewRow.supplier?.supplier_name || "—"}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Code</span>
                      <strong>{viewRow.supplier?.supplier_code || "—"}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Type</span>
                      <strong>{viewRow.supplier?.supplier_type_name || "—"}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Mobile</span>
                      <strong>{viewRow.supplier?.mobile || "—"}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Alternate Mobile</span>
                      <strong>{viewRow.supplier?.alternate_mobile || "—"}</strong>
                    </div>
                  </div>
                </div>

                <div className="outstanding-viewmodal-section">
                  <h4>Assignment & Dues</h4>
                  <div className="outstanding-viewmodal-grid">
                    <div className="outstanding-viewmodal-field">
                      <span>Assignment Type</span>
                      <strong>{viewRow.assignment_type_name || "—"}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Outstanding Amount</span>
                      <strong>{currencyFormatter.format(viewRow.outstanding_amount || 0)}</strong>
                    </div>
                    <div className="outstanding-viewmodal-field">
                      <span>Recorded On</span>
                      <strong>
                        {viewRow.created_at
                          ? dateTimeFormatter.format(new Date(viewRow.created_at))
                          : "—"}
                      </strong>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}